import React from 'react';
import LandingHeader from '../../ui/LandingHeader';
import LandingFooter from '../../ui/LandingFooter';
import CallToAction from '../CallToAction';
import { Terminal, Code2, Braces, Library, Share2 } from 'lucide-react';
import { cn } from '@/lib/utils';
import { motion } from 'framer-motion';

const features = [
  {
    title: "Professional IDE Features",
    description: "Full-featured Monaco editor with syntax highlighting, auto-completion, and multi-language support.",
    icon: Code2,
  },
  {
    title: "Python Environment",
    description: "Built-in Python runtime powered by Pyodide. No setup required, just write and run code.",
    icon: Braces,
  },
  {
    title: "Data Science Libraries",
    description: "Pre-installed libraries like Pandas, NumPy, and Matplotlib. Start analyzing data immediately.",
    icon: Library,
  },
  {
    title: "Real-Time Collaboration",
    description: "Work together in real-time with built-in version control and sharing capabilities.",
    icon: Share2,
  }
];

const sampleCode = `import pandas as pd
import plotly.express as px
import preswald

# Load the dataset
df = pd.read_csv('data/sales.csv')

# Process the data
monthly_sales = df.groupby(['date', 'category'])['revenue'].sum().reset_index()

# Create an interactive visualization
fig = px.line(monthly_sales, 
              x='date', 
              y='revenue',
              color='category',
              title='Monthly Sales by Category')

# Display in Preswald
preswald.plotly(fig)`;

const VisualEditorPage: React.FC = () => {
    const [typedCode, setTypedCode] = React.useState('');
    const [currentLine, setCurrentLine] = React.useState(0);
    const codeLines = sampleCode.split('\n');

    React.useEffect(() => {
        if (currentLine >= codeLines.length) return;

        const timer = setTimeout(() => {
            setTypedCode(prev => prev + (prev ? '\n' : '') + codeLines[currentLine]);
            setCurrentLine(prev => prev + 1);
        }, 200);

        return () => clearTimeout(timer);
    }, [currentLine]);

    return (
        <div className="min-h-screen bg-background">
            <div className="relative z-10">
                <LandingHeader />
            </div>
            
            <main className="container mx-auto px-4 sm:px-6 lg:px-8 py-6 sm:py-12 flex-grow mt-16">
                <div className="mx-auto max-w-[980px]">
                    {/* Hero Section */}
                    <div className="flex flex-col items-center text-center space-y-4 mb-8 sm:mb-12">
                        <div className="rounded-full bg-primary/10 px-3 py-1.5 text-sm font-medium text-primary">
                            Web IDE
                        </div>
                        <h1 className={cn(
                            "text-3xl font-bold tracking-tight sm:text-4xl md:text-5xl",
                            "bg-gradient-to-r from-foreground to-foreground/80 bg-clip-text text-transparent"
                        )}>
                            Code in Your Browser
                        </h1>
                        <p className="mx-auto max-w-[700px] text-lg text-muted-foreground sm:text-xl">
                            A professional Python development environment that runs entirely in your browser.
                            Write, run, and deploy data apps without any setup.
                        </p>
                    </div>

                    {/* Code Editor Demo */}
                    <div className="relative mb-16">
                        <div className="overflow-hidden rounded-lg border bg-background shadow-xl">
                            <div className="flex h-14 items-center justify-between border-b px-4">
                                <div className="flex items-center space-x-2">
                                    <div className="h-3 w-3 rounded-full bg-red-500"></div>
                                    <div className="h-3 w-3 rounded-full bg-yellow-500"></div>
                                    <div className="h-3 w-3 rounded-full bg-green-500"></div>
                                </div>
                                <div className="flex items-center space-x-3">
                                    <span className="text-sm text-muted-foreground">sales_dashboard.py</span>
                                    <Terminal className="h-5 w-5 text-muted-foreground" />
                                </div>
                            </div>
                            <div className="flex divide-x h-[500px]">
                                {/* File Tree */}
                                <div className="hidden sm:block w-48 bg-gray-50/50 p-4 font-mono text-sm">
                                    <div className="flex items-center gap-1 text-muted-foreground mb-2">
                                        <span>📁</span>
                                        <span className="font-medium">project</span>
                                    </div>
                                    <div className="pl-4 space-y-1">
                                        <div className="flex items-center gap-1 text-muted-foreground/80">
                                            <span>📁</span>
                                            <span>data</span>
                                        </div>
                                        <div className="pl-4 flex items-center gap-1 text-muted-foreground/70">
                                            <span>📊</span>
                                            <span>sales.csv</span>
                                        </div>
                                        <div className="flex items-center gap-1 text-primary font-medium">
                                            <span>🐍</span>
                                            <span>sales_dashboard.py</span>
                                        </div>
                                        <div className="flex items-center gap-1 text-muted-foreground/80">
                                            <span>⚙️</span>
                                            <span>preswald.toml</span>
                                        </div>
                                    </div>
                                </div>

                                {/* Code Editor */}
                                <div className="flex-1 bg-white overflow-auto">
                                    <div className="relative font-mono text-sm p-4">
                                        <pre className="whitespace-pre-wrap text-gray-800">
                                            <code>{typedCode}</code>
                                        </pre>
                                        {currentLine < codeLines.length && (
                                            <span className="inline-block w-2 h-4 bg-primary/80 animate-pulse ml-1" />
                                        )}
                                    </div>
                                </div>
                            </div>
                            <div className="border-t flex justify-between items-center px-4 py-2 bg-gray-50/50">
                                <div className="flex items-center gap-2">
                                    <div className="flex items-center gap-1 text-xs text-muted-foreground">
                                        <span>Python 3.11</span>
                                        <span>•</span>
                                        <span>UTF-8</span>
                                    </div>
                                </div>
                                <button className="px-3 py-1.5 text-xs bg-primary text-white rounded-md hover:bg-primary/90 transition-colors flex items-center gap-1">
                                    <span>Run</span>
                                    <span>▶</span>
                                </button>
                            </div>
                        </div>
                    </div>

                    {/* Features Grid */}
                    <div className="grid gap-6 grid-cols-1 sm:grid-cols-2 lg:gap-8 mb-16">
                        {features.map((feature, i) => (
                            <motion.div
                                key={i}
                                initial={{ opacity: 0, y: 20 }}
                                whileInView={{ opacity: 1, y: 0 }}
                                viewport={{ once: true }}
                                transition={{ duration: 0.5, delay: i * 0.1 }}
                                className="relative overflow-hidden rounded-lg border bg-background p-6"
                            >
                                <div className="flex h-12 w-12 items-center justify-center rounded-lg bg-primary/10">
                                    <feature.icon className="h-6 w-6 text-primary" />
                                </div>
                                <div className="mt-4 space-y-2">
                                    <h3 className="font-bold">{feature.title}</h3>
                                    <p className="text-muted-foreground text-sm">
                                        {feature.description}
                                    </p>
                                </div>
                            </motion.div>
                        ))}
                    </div>

                    {/* Benefits Section */}
                    <div className="text-center mb-12 sm:mb-16">
                        <h2 className={cn(
                            "text-2xl font-bold tracking-tight sm:text-3xl md:text-4xl mb-4",
                            "bg-gradient-to-r from-foreground to-foreground/80 bg-clip-text text-transparent"
                        )}>
                            Why Use a Web IDE?
                        </h2>
                        <div className="grid gap-4 sm:gap-6 grid-cols-1 sm:grid-cols-3 mt-8">
                            <div className="p-4">
                                <div className="mb-3 text-2xl">🚀</div>
                                <h3 className="font-semibold mb-2">Instant Setup</h3>
                                <p className="text-sm text-muted-foreground">
                                    No downloads or installations. Start coding in seconds.
                                </p>
                            </div>
                            <div className="p-4">
                                <div className="mb-3 text-2xl">☁️</div>
                                <h3 className="font-semibold mb-2">Cloud-Powered</h3>
                                <p className="text-sm text-muted-foreground">
                                    Access your work from anywhere, on any device.
                                </p>
                            </div>
                            <div className="p-4">
                                <div className="mb-3 text-2xl">🤝</div>
                                <h3 className="font-semibold mb-2">Collaborative</h3>
                                <p className="text-sm text-muted-foreground">
                                    Share and collaborate with your team in real-time.
                                </p>
                            </div>
                        </div>
                    </div>
                </div>
                <CallToAction />
            </main>
            <LandingFooter />
        </div>
    );
};

export default VisualEditorPage; 